import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Calendar, DollarSign, LogOut, CheckCircle, Mail, Sparkles, TrendingUp, FileText, Settings, UserCheck, CalendarDays, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Import tab components
import { LeadsTab, AppointmentsTab, PaymentsTab, EmailLogsTab, ContentTab, SettingsTab, InPersonLeadsTab, AvailabilityTab } from '../components/admin';
import JobListingTab from '../components/admin/JobListingTab';

const API = '/api';

const EnhancedAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [contents, setContents] = useState([]);
  const [surveys, setSurveys] = useState({});  // Survey data keyed by appointment_id
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  
  // Pagination state
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsTotalPages, setLeadsTotalPages] = useState(1);
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [appointmentsTotalPages, setAppointmentsTotalPages] = useState(1);
  
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate, leadsPage, appointmentsPage]);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const [analyticsRes, leadsRes, appointmentsRes, paymentsRes, contentRes, settingsRes, surveysRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, config),
        axios.get(`${API}/leads/paginated?page=${leadsPage}&limit=10`, config),
        axios.get(`${API}/appointments/paginated?page=${appointmentsPage}&limit=10`, config),
        axios.get(`${API}/payments/with-names`, config),
        axios.get(`${API}/content`, config),
        axios.get(`${API}/content/settings`),
        axios.get(`${API}/surveys/all`, config).catch(() => ({ data: [] }))  // Fetch all surveys
      ]);

      setAnalytics(analyticsRes.data);
      setLeads(leadsRes.data.leads);
      setLeadsTotalPages(leadsRes.data.pages);
      setAppointments(appointmentsRes.data.appointments);
      setAppointmentsTotalPages(appointmentsRes.data.pages);
      setPayments(paymentsRes.data);
      setContents(contentRes.data.contents || []);
      setWhatsappNumber(settingsRes.data.whatsapp_number || '');
      
      // Transform surveys array to object keyed by appointment_id
      const surveysMap = {};
      (surveysRes.data || []).forEach(survey => {
        surveysMap[survey.appointment_id] = survey;
      });
      setSurveys(surveysMap);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF4500] mx-auto mb-4"></div>
          <p className="text-[#525252]">Loading...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'availability', label: 'Availability', icon: CalendarDays },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'inperson', label: 'In-Person Leads', icon: UserCheck },
    { id: 'emails', label: 'Email Logs', icon: Mail },
    { id: 'joblisting', label: 'Job Listing', icon: Briefcase },
    { id: 'content', label: 'Content Editor', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-[#FF4500]" />
              <div>
                <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight">Admin Dashboard</h1>
                <p className="text-sm text-[#525252]">{localStorage.getItem('admin_email')}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-gray-200 text-[#0A0A0A] px-4 py-2 rounded-lg hover:bg-gray-300 transition-all duration-300"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analytics Overview */}
        {analytics && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#FF4500]" />
              Analytics Overview
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { title: 'Total Leads', value: analytics.total_leads, icon: Users, color: 'bg-[#FF4500]' },
                { title: 'Appointments', value: analytics.total_appointments, icon: Calendar, color: 'bg-[#0055FF]' },
                { title: 'Completed Calls', value: analytics.completed_calls, icon: CheckCircle, color: 'bg-[#25D366]' },
                { title: 'Payments', value: analytics.total_payments, icon: DollarSign, color: 'bg-[#FFD60A]' },
                { title: 'Total Revenue', value: `$${analytics.total_revenue}`, icon: DollarSign, color: 'bg-[#FF4500]' }
              ].map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-[#E5E5E5] hover:shadow-lg transition-all duration-300"
                  >
                    <div className={`${stat.color} w-10 h-10 rounded-lg flex items-center justify-center mb-3`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">{stat.title}</p>
                    <p className="text-2xl font-bold text-[#0A0A0A]">{stat.value}</p>
                  </motion.div>
                );
              })}
            </div>
            <div className="mt-4 bg-white rounded-xl p-6 border border-[#E5E5E5]">
              <h3 className="text-lg font-semibold mb-2">Conversion Rates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#525252] mb-1">Lead to Booking</p>
                  <p className="text-3xl font-bold text-[#FF4500]">{analytics.conversion_rate}%</p>
                </div>
                <div>
                  <p className="text-sm text-[#525252] mb-1">Booking to Payment</p>
                  <p className="text-3xl font-bold text-[#0055FF]">
                    {analytics.completed_calls > 0 
                      ? ((analytics.total_payments / analytics.completed_calls) * 100).toFixed(1)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs Container */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-100">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-4 font-semibold transition-all duration-300 flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'bg-[#FF4500] text-white'
                        : 'text-[#525252] hover:bg-gray-50'
                    }`}
                    data-testid={`${tab.id}-tab`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'leads' && (
              <LeadsTab
                leads={leads}
                leadsPage={leadsPage}
                setLeadsPage={setLeadsPage}
                leadsTotalPages={leadsTotalPages}
                fetchData={fetchData}
              />
            )}

            {activeTab === 'appointments' && (
              <AppointmentsTab
                appointments={appointments}
                appointmentsPage={appointmentsPage}
                setAppointmentsPage={setAppointmentsPage}
                appointmentsTotalPages={appointmentsTotalPages}
                fetchData={fetchData}
                surveys={surveys}
              />
            )}

            {activeTab === 'availability' && (
              <AvailabilityTab />
            )}

            {activeTab === 'payments' && (
              <PaymentsTab
                leads={leads}
                payments={payments}
                fetchData={fetchData}
              />
            )}

            {activeTab === 'inperson' && (
              <InPersonLeadsTab />
            )}

            {activeTab === 'emails' && (
              <EmailLogsTab />
            )}

            {activeTab === 'joblisting' && (
              <JobListingTab />
            )}

            {activeTab === 'content' && (
              <ContentTab
                contents={contents}
                whatsappNumber={whatsappNumber}
                setWhatsappNumber={setWhatsappNumber}
                fetchData={fetchData}
              />
            )}

            {activeTab === 'settings' && (
              <SettingsTab />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedAdminDashboard;
